import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable } from 'rxjs';
import { EnvironmentToken } from '../../../app.config';
import {
  ControlPointCheckinView,
  ControlPointCheckinViewSchema,
} from '@fsf/types';
import { z } from 'zod';
import {
  ControlPoint,
  ControlPointCheckinRow,
  StoreControlPointPayload,
  UpdateControlPointPayload,
} from '../../models/control-points/control-point.model';

@Injectable({ providedIn: 'root' })
export class ControlPointService {
  #http = inject(HttpClient);
  #apiUrl = inject(EnvironmentToken).apiUrl;

  #items = new BehaviorSubject<ControlPoint[]>([]);
  public items$ = this.#items.asObservable();
  public items = toSignal(this.items$, { initialValue: [] });

  #checkins = new BehaviorSubject<ControlPointCheckinRow[]>([]);
  public checkins$ = this.#checkins.asObservable();
  public checkins = toSignal(this.checkins$, {
    initialValue: [] as ControlPointCheckinRow[],
  });

  getAll() {
    this.#http
      .get<{ data: ControlPoint[] }>(
        `${this.#apiUrl}/api/admin/control-points`,
      )
      .subscribe({
        next: (response) => this.#items.next(response.data ?? []),
        error: (e) => console.log(e),
      });
  }

  /**
   * Load the read-only check-in history (most recent 200 rows,
   * reverse-chronological). Rows are Zod-validated against the generated
   * ControlPointCheckinView schema, then flat helper fields (trainer name)
   * are projected AFTER validation — mirroring the defects-page
   * responsible_user_name pattern. control_point_name is already on the view.
   */
  getCheckins() {
    this.#http
      .get<{ data: ControlPointCheckinView[] }>(
        `${this.#apiUrl}/api/admin/control-points/checkins`,
      )
      .subscribe({
        next: (response) => {
          const validated = z
            .array(ControlPointCheckinViewSchema)
            .parse(response.data ?? []);
          const rows: ControlPointCheckinRow[] = validated.map((c) => ({
            ...c,
            user_name: c.user?.name ?? null,
          }));
          this.#checkins.next(rows);
        },
        error: (e) => console.log(e),
      });
  }

  getById(id: number): Observable<{ data: ControlPoint }> {
    return this.#http.get<{ data: ControlPoint }>(
      `${this.#apiUrl}/api/admin/control-points/${id}`,
    );
  }

  create(
    payload: StoreControlPointPayload,
  ): Observable<{ data: ControlPoint }> {
    return this.#http.post<{ data: ControlPoint }>(
      `${this.#apiUrl}/api/admin/control-points`,
      payload,
    );
  }

  update(
    id: number,
    payload: UpdateControlPointPayload,
  ): Observable<{ data: ControlPoint }> {
    return this.#http.put<{ data: ControlPoint }>(
      `${this.#apiUrl}/api/admin/control-points/${id}`,
      payload,
    );
  }

  delete(id: number): Observable<unknown> {
    return this.#http.delete(
      `${this.#apiUrl}/api/admin/control-points/${id}`,
    );
  }

  /**
   * Record an NFC check-in for the given tag name.
   *
   * POST /api/control-points/checkin { name: tagName }.
   * On success the API returns 201 with the created check-in. Errors are
   * surfaced as HttpErrorResponse to the caller: 403 carries either the
   * `{ error: 'missing_qualifications', missing: string[] }` body (unqualified
   * trainer) or a plain 403 (lacks controlpoints.checkin); 404 is an unknown
   * tag; 0/5xx is a connectivity/server failure.
   */
  checkin(
    tagName: string,
  ): Observable<{ data: ControlPointCheckinView }> {
    return this.#http.post<{ data: ControlPointCheckinView }>(
      `${this.#apiUrl}/api/control-points/checkin`,
      { name: tagName },
    );
  }
}
